import type { HttpErrorResponse } from '@angular/common/http';

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, map, retry, tap } from 'rxjs/operators';
import { FeeItem } from 'src/app/shared/interfaces/account/account.interface';
import {
  IBatchRemittance,
  IProviderAPI,
  IProviderStatusDates,
} from 'src/app/shared/interfaces/provider/provider.interfaces';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ProviderGroupGQLService } from 'src/app/shared/services/provider/provider-group.graphql.service';
import { ProviderService } from 'src/app/shared/services/provider/provider.service';
import { UserStateService } from 'src/app/shared/services/users/user-state.service';
import { EMAIL_REGEX } from 'src/app/shared/variables/variables';

import { QualifiersGridComponent } from '../../qualifiers-grid/qualifiers-grid.component';

@Component({
  selector: 'app-add-provider-setup-modal',
  templateUrl: './add-provider-setup-modal.component.html',
  styleUrls: ['./add-provider-setup-modal.component.scss'],
})
export class AddProviderSetupModalComponent implements OnInit, OnDestroy {
  @Input() providerStatusDates?: IProviderStatusDates;
  @Input() batchRemittance?: IBatchRemittance;

  @ViewChild(QualifiersGridComponent) qualifiersGrid: QualifiersGridComponent;

  predefinedData?: Partial<any>;
  groupId?: string;

  isClientAdmin?: boolean;
  isFirstStep = true;
  isLoading = true;

  providerForm: UntypedFormGroup;

  fees$: Observable<FeeItem[]>;

  private subscriptions: Subscription = new Subscription();

  private isProviderSaved = false;
  private accountNumber: string;
  private savedProvider: IProviderAPI;

  constructor(
    private alertService: AlertService,
    private fb: UntypedFormBuilder,
    private providerGroupGQLService: ProviderGroupGQLService,
    private providerService: ProviderService,
    private activeModal: NgbActiveModal,
    private userStateService: UserStateService,
  ) {}

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnInit() {
    this.isClientAdmin = false;
    const currentUser = this.userStateService.user;
    const permissions = currentUser?.permissions;
    this.isClientAdmin = permissions?.some((permission) => {
      return permission.name === 'Client Admin - Accounts';
    });

    this.initForms();

    if (this.groupId) {
      this.subscriptions.add(
        this.providerGroupGQLService
          .getProviderGroupDataForProvider(this.groupId)
          .pipe(
            map((x) => {
              this.predefinedData = {
                ...x,
                accountNumber: x.accountNumber,
                groupId: x.groupId,
                pms: x.pMS,
                feeSchedule: x.feeSchedule,
                certified: x.certified,
                contact: {
                  title: x.contactTitle,
                  name: x.contactName,
                  email: x.contactEmail,
                  fax: x.contactFax,
                  phone: x.contactPhone,
                },
                address: {
                  line1: x.addressLine1,
                  line2: x.addressLine2,
                  city: x.addressCity,
                  state: x.addressState,
                  zipcode: x.addressZipcode,
                },
                ecsStatus: x.eCSStatus,
                ecsDate: x.eCSDate,
                eRAStatus: x.eRAStatus,
                eRADate: x.eRADate,
              };

              return this.predefinedData;
            }),
          )
          .subscribe({
            next: () => {
              this.setPredefinedData();
              this.isLoading = false;
            },
            error: (error: unknown) => {
              this.alertService.error(error as HttpErrorResponse | string);
              return of<Partial<IProviderAPI> | undefined>(undefined);
            },
          }),
      );
    }

    this.fees$ = this.providerGroupGQLService.getFees().pipe(
      retry(1),
      catchError((error) => {
        return of(error);
      }),
    );
  }

  save() {
    if (this.providerForm.invalid) {
      return;
    }

    const providerForm = this.providerForm.getRawValue();

    const provider = {
      ...providerForm,
      accountNumber: this.accountNumber,
      providerGroupId: this.groupId,
      eCSStatus: providerForm.ecsStatus,
      eRAStatus: providerForm.eraStatus,
      eCSDate: providerForm.ecsDate,
      eRADate: providerForm.eraDate,
      claimStatusStatus: providerForm.claimStatusStatus,
      eligibilityStatus: providerForm.eligibilityStatus,
      statementStatus: providerForm.statementStatus,
      certified: providerForm.certified || 'T',
    } as IProviderAPI;

    this.saveProvider(provider)
      .pipe(
        concatMap((provider) => {
          return this.qualifiersGrid.saveQualifiers(provider.id, this.groupId);
        }),
      )
      .subscribe({
        next: (result) => {
          this.isLoading = false;

          if (result) {
            this.activeModal.close(() => {
              return this.saveProvider(provider);
            });
          }
        },
        error: (error: unknown) => {
          this.isLoading = false;
          this.alertService.error(error as HttpErrorResponse | string);
        },
      });
  }

  private initForms() {
    this.providerForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      mi: [''],
      degree: [''],
      pms: [{ value: '', disabled: true }],
      contact: this.fb.group({
        name: [''],
        phone: ['', [Validators.maxLength(10)]],
        fax: [''],
        email: ['', Validators.pattern(EMAIL_REGEX)],
        title: [''],
      }),
      ecsStatus: [''],
      ecsDate: [''],
      eraStatus: [''],
      eraDate: [''],
      claimStatusStatus: [''],
      claimStatusDate: [''],
      eligibilityStatus: [''],
      eligibilityDate: [''],
      statementStatus: [''],
      statementDate: [''],
      certified: [{ value: 'T', disabled: true }, Validators.required],
      feeSchedule: [{ value: '' }, Validators.required],
      address: this.fb.group({
        line1: [''],
        line2: [''],
        zipcode: [''],
        state: [''],
        city: [''],
      }),
    });
    this.providerForm.markAllAsTouched();
  }

  private setPredefinedData() {
    this.accountNumber = this.predefinedData?.accountNumber;
    this.groupId = this.predefinedData?.groupId;

    if (this.providerStatusDates) {
      this.providerForm.patchValue(this.providerStatusDates);
    }

    if (this.batchRemittance) {
      this.providerForm.patchValue(this.batchRemittance);
    }

    if (this.predefinedData) {
      this.providerForm.patchValue({
        pms: this.predefinedData.pMS,
        feeSchedule: this.predefinedData.feeSchedule,
        certified: this.predefinedData.certified,
        contact: this.predefinedData.contact,
        address: this.predefinedData.address,
      });
    }
  }
  private saveProvider(provider: IProviderAPI): Observable<IProviderAPI> {
    if (this.isProviderSaved) {
      return of<IProviderAPI>(this.savedProvider);
    }

    return this.providerService.createProvider(provider).pipe(
      tap((provider) => {
        this.savedProvider = provider;
        this.isProviderSaved = true;
        this.alertService.success('Provider created successfully');
      }),
    );
  }

  close() {
    this.activeModal.dismiss();
  }

  next() {
    this.isFirstStep = false;
  }

  previous() {
    this.isFirstStep = true;
  }
}
